import { useEffect, useRef, useState } from 'react';
import { DEFAULT_TUNING, TUNING_RANGES, type AudioTuning } from '../voice/tuning';
import type { VoiceSession, VoiceState } from '../voice/VoiceSession';

const PREFS = 'elixir.audioPrefs';
const savePref = (patch: Record<string, string>) => {
  try {
    const cur = JSON.parse(localStorage.getItem(PREFS) ?? '{}');
    localStorage.setItem(PREFS, JSON.stringify({ ...cur, ...patch }));
  } catch {
    /* ignore */
  }
};

export function SettingsDialog({
  voice,
  state: v,
  isHost,
  hostLink,
  onClose,
  onLeave,
  onCopied,
}: {
  voice: VoiceSession;
  state: VoiceState;
  isHost: boolean;
  hostLink: string | null;
  onClose: () => void;
  onLeave: () => void;
  onCopied: (msg: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [showTuning, setShowTuning] = useState(() => new URLSearchParams(location.search).has('tune'));

  useEffect(() => {
    const d = ref.current!;
    d.showModal();
    return () => d.close();
  }, []);

  return (
    <dialog ref={ref} className="dialog" onClose={onClose} onCancel={onClose}>
      <div className="dialog-head">
        <h2>Audio settings</h2>
        <button className="link-btn" onClick={onClose}>
          Done
        </button>
      </div>

      <label>
        Microphone
        <select
          value={v.inputId}
          onChange={(e) => {
            savePref({ inputId: e.target.value });
            void voice.setInputDevice(e.target.value);
          }}
        >
          <option value="default">System default</option>
          {v.inputs
            .filter((d) => d.deviceId !== 'default')
            .map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || 'Microphone'}
              </option>
            ))}
        </select>
      </label>

      {v.canPickOutput ? (
        <label>
          Speakers or headphones
          <select
            value={v.outputId}
            onChange={(e) => {
              savePref({ outputId: e.target.value });
              void voice.setOutputDevice(e.target.value);
            }}
          >
            <option value="default">System default</option>
            {v.outputs
              .filter((d) => d.deviceId !== 'default')
              .map((d) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label || 'Output'}
                </option>
              ))}
          </select>
        </label>
      ) : (
        <p className="muted small">This browser plays sound through your system output. Change it in your computer's sound settings.</p>
      )}

      {isHost && hostLink && (
        <div className="host-link">
          <p className="small">
            Your host link opens this quiz as quizmaster on another device. Keep it private: anyone with it can run the quiz.
          </p>
          <button
            className="btn btn-quiet"
            onClick={() => {
              void navigator.clipboard?.writeText(hostLink);
              onCopied('Host link copied. Keep it private.');
            }}
          >
            Copy host link
          </button>
        </div>
      )}

      <details open={showTuning} onToggle={(e) => setShowTuning((e.target as HTMLDetailsElement).open)}>
        <summary>Fine-tune whisper mixing</summary>
        <p className="muted small">For calibrating during a test call. Changes apply instantly and are saved in this browser only.</p>
        {(Object.keys(TUNING_RANGES) as (keyof AudioTuning)[]).map((k) => {
          const r = TUNING_RANGES[k];
          return (
            <label key={k} className="slider">
              <span>
                {r.label} <strong>{v.tuning[k]}</strong>
              </span>
              <input
                type="range"
                min={r.min}
                max={r.max}
                step={r.step}
                value={v.tuning[k]}
                onChange={(e) => voice.updateTuning({ [k]: Number(e.target.value) })}
              />
            </label>
          );
        })}
        <button className="btn btn-quiet" onClick={() => voice.updateTuning(DEFAULT_TUNING)}>
          Reset to defaults
        </button>
      </details>

      <button className="btn btn-danger" onClick={onLeave}>
        Leave quiz
      </button>
    </dialog>
  );
}
